import React, { useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';

import { Swiper, SwiperSlide } from 'swiper/react';

import SwiperSlideUnstyled from '@ecp/common/src/components/unstyled/swiper/SwiperSlideUnstyled';
import useBrandSwipe from '@ecp/common/src/components/unstyled/swiper/useBrandSwipe';
import SwiperItem from '@ecp/common/src/components/swiper/component/SwiperItem';

const SwiperBrandUnstyled = React.forwardRef(({ onClick, ...props }, ref) => {
  const {
    width,
    height,
    round,
    backgroundColor,
    itemList,
    itemProps,
    itemTitleProps,
    pagingProps,
    rootComponent: RootComponent,
    wrapperComponent: WrapperComponent,
    controllerComponent: ControllerComponent,
    pagingComponent: PagingComponent,
    itemComponent: Item,
    rootProps,
    carouselProps,
    controlProps,
    adultGood,
    initialSlide,
    tagGb,
    title,
    goodName,
    handleCartPopupSuccess,
    handlerMessageCardChange,
  } = props;

  const sliderRef = useRef(null);
  useImperativeHandle(ref, () => sliderRef.current);
  const {
    selectedIndex,
    showControl,
    totalSlideCount,
    handleItemClick,
    getControlProps,
    getCarouselProps,
    getSlideProps,
    getPagingProps,
    handlePrev,
    handleNext,
    handlePaging,
  } = useBrandSwipe(props, sliderRef, onClick);

  return (
    <RootComponent {...rootProps}>
      <WrapperComponent
        width={width}
        height={height}
        round={round}
        backgroundColor={backgroundColor}
        data-testid="carousel-wrapper"
        {...props}
      >
        {!!itemList.length && (
          <Swiper ref={sliderRef} {...getCarouselProps(carouselProps)}>
            {itemList.map((item, index) => (
              <SwiperSlide key={`swiper-slide-item__${index}`} {...getSlideProps(itemProps)} aria-label={''}>
                <SwiperSlideUnstyled
                  index={index}
                  item={item}
                  current={initialSlide !== undefined ? initialSlide : selectedIndex}
                  total={totalSlideCount}
                  onClick={handleItemClick}
                  tagGb={tagGb}
                  title={title}
                >
                  <Item
                    item={item}
                    itemProps={itemProps}
                    itemTitleProps={itemTitleProps}
                    adultGood={adultGood}
                    index={index}
                    total={totalSlideCount}
                    goodName={goodName}
                    handleCartPopupSuccess={handleCartPopupSuccess}
                    handlerMessageCardChange={handlerMessageCardChange}
                  />
                </SwiperSlideUnstyled>
              </SwiperSlide>
            ))}
          </Swiper>
        )}
        {showControl && (
          <ControllerComponent
            {...getControlProps(controlProps)}
            data-testid="carousel-controller"
            itemList={itemList}
            handleItemClick={handleItemClick}
            btnViewAllFlag={props?.btnViewAllFlag}
            itemComponent={Item}
          />
        )}
        {PagingComponent && (
          <PagingComponent
            {...getPagingProps(pagingProps)}
            handlePrev={handlePrev}
            handleNext={handleNext}
            handlePaging={handlePaging}
            data-testid="carousel-paging"
          />
        )}
      </WrapperComponent>
    </RootComponent>
  );
});

SwiperBrandUnstyled.propTypes = {
  itemList: PropTypes.array,
  itemProps: PropTypes.object,
  itemTitleProps: PropTypes.object,
  pagingProps: PropTypes.object,
  onClick: PropTypes.func,
  rootComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element, PropTypes.string]),
  wrapperComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element, PropTypes.string]),
  controllerComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
  pagingComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),

  itemComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
  carouselProps: PropTypes.object,
  width: PropTypes.string,
  height: PropTypes.string,
  round: PropTypes.bool,
  backgroundColor: PropTypes.string,
  initialSlide: PropTypes.number,
  rootProps: PropTypes.object,
  controlProps: PropTypes.object,
  adultGood: PropTypes.bool,
  tagGb: PropTypes.oneOf(['button']),
  title: PropTypes.string,
  btnViewAllFlag: PropTypes.string,
  goodName: PropTypes.string,
  handleCartPopupSuccess: PropTypes.func,
  handlerMessageCardChange: PropTypes.func,
};

SwiperBrandUnstyled.defaultProps = {
  itemList: [],
  controlProps: {},
  rootComponent: 'div',
  wrapperComponent: 'div',
  itemComponent: SwiperItem,
  onClick: () => {},
  itemTitleProps: {},
  hideUnusableArrow: false,
};

SwiperBrandUnstyled.displayName = 'SwiperBrandUnstyled';

export default SwiperBrandUnstyled;
